import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Clock, FileDown, Mail, MessageCircle, Plus, Trash2, Send, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

const download = async (url, name) => {
  const r = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data); a.download = name; a.click();
  URL.revokeObjectURL(a.href);
};

const StatusIcon = ({ status }) => (status === "envoyé" ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 inline" /> : status === "non configuré" ? <AlertTriangle className="w-3.5 h-3.5 text-orange-500 inline" /> : <XCircle className="w-3.5 h-3.5 text-red-600 inline" />);

export const NightlyReportsCard = () => {
  const [data, setData] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/reports/nightly").then((r) => { setData(r.data); setRecipients(r.data.recipients.filter((x) => x.source === "paramètres").map(({ name, email, whatsapp }) => ({ name, email, whatsapp }))); }).catch(() => toast.error("Erreur lors du chargement des rapports"));
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/reports/nightly/run");
      const ok = r.data.emails.filter((e) => e.status === "envoyé").length;
      toast.success(`Sauvegarde + ${r.data.files.length} PDF générés — ${ok}/${r.data.emails.length} e-mail(s) envoyé(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'exécution"); }
    finally { setRunning(false); }
  };

  const saveRecipients = async () => {
    setSaving(true);
    try { await api.put("/settings/report-recipients", { recipients }); toast.success("Destinataires enregistrés"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const setR = (i, k, v) => setRecipients(recipients.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  if (!data) return null;
  const auto = data.recipients.filter((r) => r.source !== "paramètres");

  return (
    <Card className="lg:col-span-3" data-testid="nightly-reports-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
        <div>
          <CardTitle className="font-[Chivo] text-base flex items-center gap-2"><Send className="w-4 h-4" /> Rapports nocturnes automatiques</CardTitle>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {data.schedule}</p>
          <p className="text-xs mt-1 flex items-center gap-3">
            <span className={data.email_configured ? "text-green-700" : "text-red-600"}><Mail className="w-3 h-3 inline mr-1" />E-mail {data.email_configured ? "actif" : "non configuré"}</span>
            <span className={data.whatsapp_configured ? "text-green-700" : "text-orange-600"} data-testid="whatsapp-status"><MessageCircle className="w-3 h-3 inline mr-1" />WhatsApp {data.whatsapp_configured ? "actif" : "en attente des identifiants Twilio"}</span>
          </p>
        </div>
        <Button onClick={runNow} disabled={running} className="btn-primary" data-testid="run-nightly-btn">{running ? <div className="spinner w-4 h-4" /> : <><Send className="w-4 h-4 mr-2" /> Lancer maintenant</>}</Button>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <p className="overline mb-2">Rapports PDF (téléchargement immédiat)</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.kinds).map(([k, label]) => (
              <Button key={k} variant="outline" size="sm" className="rounded-sm" onClick={() => download(`/reports/pdf/${k}`, `${k}.pdf`).catch(() => toast.error("Erreur de téléchargement"))} data-testid={`download-report-${k}`}><FileDown className="w-3.5 h-3.5 mr-1" /> {label}</Button>
            ))}
          </div>
        </div>

        <div>
          <p className="overline mb-2">Destinataires</p>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="auto-recipients">
            {auto.map((r) => <span key={r.email} className="px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-700" title={r.source}>{r.name} · {r.email}</span>)}
            {auto.length === 0 && <span className="text-xs text-slate-400">Aucun admin / gérant avec e-mail</span>}
          </div>
          <p className="text-xs text-slate-500 mb-2">Destinataires supplémentaires (e-mail et/ou numéro WhatsApp, ex. +221 77 000 00 00) :</p>
          <div className="space-y-2">
            {recipients.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`recipient-row-${i}`}>
                <Input className="col-span-3 h-8 rounded-sm" placeholder="Nom" value={r.name} onChange={(e) => setR(i, "name", e.target.value)} data-testid={`recipient-name-${i}`} />
                <Input className="col-span-4 h-8 rounded-sm" placeholder="E-mail" value={r.email} onChange={(e) => setR(i, "email", e.target.value)} data-testid={`recipient-email-${i}`} />
                <Input className="col-span-4 h-8 rounded-sm font-mono" placeholder="WhatsApp +221…" value={r.whatsapp} onChange={(e) => setR(i, "whatsapp", e.target.value)} data-testid={`recipient-whatsapp-${i}`} />
                <Button variant="ghost" size="sm" className="col-span-1 h-8 text-red-600" onClick={() => setRecipients(recipients.filter((_, j) => j !== i))} data-testid={`recipient-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setRecipients([...recipients, { name: "", email: "", whatsapp: "" }])} data-testid="recipient-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>
            <Button size="sm" className="btn-primary" onClick={saveRecipients} disabled={saving} data-testid="recipients-save-btn">Enregistrer les destinataires</Button>
          </div>
        </div>

        <div>
          <p className="overline mb-2">Historique des envois</p>
          {data.runs.length === 0 ? <p className="text-xs text-slate-400">Aucun envoi pour l'instant.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="nightly-runs-table">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide"><tr><th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2 py-1.5">Déclenchement</th><th className="text-left px-2 py-1.5">Sauvegarde</th><th className="text-left px-2 py-1.5">PDF</th><th className="text-left px-2 py-1.5">E-mails</th><th className="text-left px-2 py-1.5">WhatsApp</th></tr></thead>
                <tbody>
                  {data.runs.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`nightly-run-${r.id}`}>
                      <td className="px-2 py-1.5 font-mono whitespace-nowrap">{fmt(r.started_at)}</td>
                      <td className="px-2 py-1.5">{r.trigger}</td>
                      <td className="px-2 py-1.5">{r.backup?.file ? <span className="text-green-700">{r.backup.documents} enreg.</span> : <span className="text-red-600">échec</span>}</td>
                      <td className="px-2 py-1.5"><div className="flex flex-wrap gap-1">{r.files.map((f) => <button key={f.file} type="button" onClick={() => window.open(f.url, "_blank", "noopener")} className="text-green-700 underline" data-testid={`run-file-${f.kind}`}>{f.label}</button>)}</div></td>
                      <td className="px-2 py-1.5">{r.emails.map((e, i) => <div key={i} title={e.status}><StatusIcon status={e.status} /> {e.to}</div>)}</td>
                      <td className="px-2 py-1.5">{r.whatsapp.length === 0 ? <span className="text-slate-400">—</span> : r.whatsapp.map((w, i) => <div key={i} title={w.status}><StatusIcon status={w.status} /> {w.to}</div>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.runs[0]?.errors?.length > 0 && <p className="text-xs text-red-600 mt-2">Dernières erreurs : {data.runs[0].errors.join(" · ")}</p>}
        </div>
      </CardContent>
    </Card>
  );
};
